import React, { useRef, useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';

const SelfieCapture = ({ show, onHide, onCapture }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [error, setError] = useState(null);
  const [videoReady, setVideoReady] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState(null);
  const [showPermissionInstructions, setShowPermissionInstructions] = useState(false);

  useEffect(() => {
    if (show) {
      checkPermissionAndStartCamera();
    } else {
      stopCamera();
    }
  }, [show]);

  // Check camera permission status
  const checkCameraPermission = async () => {
    try {
      // Check if Permissions API is supported
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({ name: 'camera' });
        setPermissionStatus(result.state);
        
        // Listen for permission changes
        result.onchange = () => {
          setPermissionStatus(result.state);
          if (result.state === 'granted') {
            // Permission was granted, try to start camera
            startCamera();
          }
        };
        
        return result.state;
      }
    } catch (err) {
      console.log('Permissions API not fully supported:', err);
    }
    return null;
  };

  // Check permission before starting camera
  const checkPermissionAndStartCamera = async () => {
    const status = await checkCameraPermission();
    
    // If permission is denied, show instructions
    if (status === 'denied') {
      setError('Camera permission was denied. Please enable camera access in your browser settings.');
      setShowPermissionInstructions(true);
      return;
    }
    
    // Try to start camera
    await startCamera();
  };

  // Ensure video plays when retaking (when capturedImage becomes null)
  useEffect(() => {
    if (!capturedImage && stream && videoRef.current) {
      const video = videoRef.current;
      if (video.paused) {
        video.play().catch(err => {
          console.error('Error playing video:', err);
        });
      }
    }
  }, [capturedImage, stream]);

  const startCamera = async () => {
    setVideoReady(false);
    setShowPermissionInstructions(false);

    try {
      setError(null);

      // Stop any existing stream first
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      });

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.play().catch(err => {
          console.error('Error playing video:', err);
        });
      }

      setStream(mediaStream);
      setVideoReady(true);
      setPermissionStatus('granted');
    } catch (err) {
      console.error('Error accessing camera:', err);
      
      // Handle different error types
      let errorMessage = 'Unable to access camera.';
      let showInstructions = false;

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'Camera permission was denied. Please enable camera access in your browser settings and try again.';
        showInstructions = true;
        setPermissionStatus('denied');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage = 'No camera found. Please ensure a camera is connected.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage = 'Camera is already in use by another application. Please close other apps using the camera and try again.';
      } else if (err.name === 'OverconstrainedError' || err.name === 'ConstraintNotSatisfiedError') {
        errorMessage = 'Camera does not support the required settings.';
      } else {
        errorMessage = 'Unable to access camera. Please check your camera permissions and try again.';
      }

      setError(errorMessage);
      setShowPermissionInstructions(showInstructions);
    }
  };

  const stopCamera = () => {
    setVideoReady(false);

    if (stream) {
      stream.getTracks().forEach(track => {
        track.stop();
      });

      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      setStream(null);
    }
    setCapturedImage(null);
    setError(null);
    setShowPermissionInstructions(false);
    setPermissionStatus(null);
  };

  // Check if video is ready for capture
  const isVideoReady = () => {
    if (!videoRef.current || !stream) return false;

    const video = videoRef.current;
    const videoTrack = stream.getVideoTracks()[0];

    if (!videoTrack || videoTrack.readyState !== 'live') {
      return false;
    }

    if (video.videoWidth > 0 && video.videoHeight > 0 && !video.paused) {
      return true;
    }

    return false;
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current && stream) {
      const video = videoRef.current;
      const canvas = canvasRef.current;

      const videoTrack = stream.getVideoTracks()[0];
      if (!videoTrack || videoTrack.readyState !== 'live') {
        setError('Camera is not active. Please wait a moment and try again.');
        return;
      }

      if (video.videoWidth === 0 || video.videoHeight === 0) {
        setError('Video not ready. Please wait a moment and try again.');
        return;
      }

      try {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const context = canvas.getContext('2d');
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageDataUrl = canvas.toDataURL('image/jpeg', 0.9);
        setCapturedImage(imageDataUrl);
        setError(null);
      } catch (err) {
        console.error('Error capturing photo:', err);
        setError('Error capturing photo. Please try again.');
      }
    } else {
      setError('Camera not ready. Please wait a moment and try again.');
    }
  };

  const retakePhoto = async () => {
    setCapturedImage(null);
    setError(null);
    setVideoReady(false);
    setShowPermissionInstructions(false);
    await checkPermissionAndStartCamera();
  };

  const handleRetry = async () => {
    setError(null);
    setShowPermissionInstructions(false);
    await checkPermissionAndStartCamera();
  };

  const confirmPhoto = () => {
    if (capturedImage) {
      fetch(capturedImage)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], `selfie-${Date.now()}.jpg`, { type: 'image/jpeg' });

          stopCamera();
          onCapture(file);

          setTimeout(() => {
            onHide();
          }, 100);
        })
        .catch(err => {
          console.error('Error converting image:', err);
          setError('Error processing image. Please try again.');
        });
    }
  };

  const handleClose = () => {
    stopCamera();
    setTimeout(() => {
      onHide();
    }, 100);
  };

  // Get browser-specific instructions
  const getBrowserInstructions = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    let browser = 'unknown';
    
    if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
      browser = 'chrome';
    } else if (userAgent.includes('firefox')) {
      browser = 'firefox';
    } else if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
      browser = 'safari';
    } else if (userAgent.includes('edg')) {
      browser = 'edge';
    }

    const instructions = {
      chrome: 'Click the lock icon in the address bar → Site settings → Camera → Allow',
      firefox: 'Click the lock icon in the address bar → Permissions → Camera → Allow',
      safari: 'Safari → Preferences → Websites → Camera → Allow for this website',
      edge: 'Click the lock icon in the address bar → Permissions → Camera → Allow',
      unknown: 'Go to your browser settings and enable camera permissions for this website'
    };

    return instructions[browser] || instructions.unknown;
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Take a Selfie for Punch-In</Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center">
        {error && (
          <div className="alert alert-danger" role="alert">
            <div className="d-flex align-items-start">
              <i className="bi bi-exclamation-triangle-fill me-2 mt-1"></i>
              <div className="flex-grow-1">
                <div>{error}</div>
                {showPermissionInstructions && (
                  <div className="mt-2">
                    <small className="d-block text-muted mb-2">
                      <strong>How to enable camera access:</strong>
                    </small>
                    <small className="d-block text-muted mb-3">
                      {getBrowserInstructions()}
                    </small>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleRetry}
                      className="me-2"
                    >
                      <i className="bi bi-arrow-clockwise me-1"></i>
                      Try Again
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="position-relative" style={{ maxWidth: '640px', margin: '0 auto' }}>
          {!capturedImage ? (
            <>
              {!error && (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  onLoadedMetadata={() => {
                    setVideoReady(true);
                  }}
                  style={{
                    width: '100%',
                    maxHeight: '480px',
                    borderRadius: '8px',
                    backgroundColor: '#000'
                  }}
                />
              )}
              {error && !videoReady && (
                <div
                  style={{
                    width: '100%',
                    height: '360px',
                    backgroundColor: '#000',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff'
                  }}
                >
                  <div className="text-center">
                    <i className="bi bi-camera-video-off" style={{ fontSize: '3rem' }}></i>
                    <p className="mt-3">Camera not available</p>
                  </div>
                </div>
              )}
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </>
          ) : (
            <img
              src={capturedImage}
              alt="Captured selfie"
              style={{
                width: '100%',
                maxHeight: '480px',
                borderRadius: '8px'
              }}
            />
          )}
        </div>

        {!error && (
          <div className="mt-3">
            {!capturedImage ? (
              <Button
                variant="primary"
                size="lg"
                onClick={capturePhoto}
                disabled={!videoReady}
              >
                <i className="bi bi-camera-fill me-2"></i>
                {videoReady ? 'Capture Photo' : 'Preparing Camera...'}
              </Button>
            ) : (
              <div className="d-flex gap-2 justify-content-center">
                <Button
                  variant="outline-secondary"
                  onClick={retakePhoto}
                >
                  <i className="bi bi-arrow-counterclockwise me-2"></i>
                  Retake
                </Button>
                <Button
                  variant="success"
                  onClick={confirmPhoto}
                >
                  <i className="bi bi-check-circle-fill me-2"></i>
                  Use This Photo
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SelfieCapture;